import type { TFunction } from "i18next";
import { DATE_FILTER, TEXT_FILTER } from "@/lib/columnFilters";
import { fmtDateTime } from "@/lib/dates";
import type { ListColumn } from "./ListTable";

// "Laatst gewijzigd" and "Gewijzigd door" (Camiel, 22-09-2026: "laatst gewijzigd toevoegen als
// kolom overal en gewijzigd door"): the last two columns of every list. Every row carries who
// changed it last and when — a person, or TALIS, AFAS or DataBridge when a batch did — as the
// API stamps it on every write (services/api lib/changeStamp.ts). A change from before
// 22-09-2026 has a time and nobody.

export type ChangeVia = "wlx" | "talis" | "afas" | "databridge" | "scoretrace" | "portal" | "customer";

/** Who changed a row last and when, as every list sends it (services/api lib/changes.ts). */
export interface Changed {
  changedAt: string | null;
  /** The person; null when a system changed it, or nobody knows. */
  changedBy: { id: string; name: string } | null;
  changedVia: ChangeVia | null;
}

export type ChangedColumnId = "changedAt" | "changedBy";

/** Last in a list's columns. The name does not sort: the API has it by id, not by name. */
export const CHANGED_COLUMNS: readonly (ListColumn & { id: ChangedColumnId })[] = [
  { id: "changedAt", labelKey: "common.changedAt", sortKey: "changedAt", descFirst: true, filter: DATE_FILTER, defaultWidth: 150 },
  { id: "changedBy", labelKey: "common.changedBy", filter: TEXT_FILTER, filterKey: "changedBy", defaultWidth: 170 },
];

const VIA_KEYS: Record<ChangeVia, string> = {
  wlx: "common.changedViaWlx",
  talis: "common.changedViaTalis",
  afas: "common.changedViaAfas",
  databridge: "common.changedViaDatabridge",
  scoretrace: "common.changedViaScoretrace",
  portal: "common.changedViaPortal",
  customer: "common.changedViaCustomer",
};

/** Who, in words: the person, else the system, else nobody. */
export function changedByText(row: Changed, t: TFunction): string {
  if (row.changedBy) return row.changedBy.name;
  return row.changedVia ? t(VIA_KEYS[row.changedVia] as never) : "—";
}

/** The two cells, for a list's `cell(id, row)`. */
export function changedCell(id: ChangedColumnId, row: Changed, t: TFunction) {
  if (id === "changedAt") {
    return (
      <td key={id} className="mono">
        {row.changedAt ? fmtDateTime(new Date(row.changedAt)) : "—"}
      </td>
    );
  }
  return (
    <td key={id} title={row.changedBy ? undefined : row.changedVia ? t("common.changedBySystem") : undefined}>
      <span className={row.changedBy ? undefined : "t-muted"}>{changedByText(row, t)}</span>
    </td>
  );
}

/**
 * What a list that sorts and filters in the browser reads (lib/sortRows.ts, filterRows): the
 * moment, and the person's name or the system's code, which reads as its name ("talis" finds TALIS).
 */
export const CHANGED_VALUES = {
  changedAt: (row: Changed) => row.changedAt,
  changedBy: (row: Changed) => row.changedBy?.name ?? row.changedVia,
};
